import * as fromRoot from '../../shared/reducers';
import {ActionReducerMap, createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import * as _ from 'lodash';
import {MemoizedSelectorWithProps} from '@ngrx/store/src/selector';
import {SiteFilter} from '../models/site-filter';
import {Site} from '../../shared/models/site';
import {SitesListReducer, sitesListSelectors, SitesListState} from './sites-list';
import {SelectedItem} from '../../order/models/order';
import {SiteSelectedState, SitesSelectedReducer, sitesSelectedSelectors} from './sites-selected';
import {SiteReducer, siteSelectors, SiteState} from './site';
import {PlayersState, PlayersReducer, playersSelectors} from './players-list';
import {Player} from '../../shared/models/player';
import {deviceModelsReducer, deviceModelsSelectors, DeviceModelsState} from './device-models';
import {GroupRole} from '../../shared/models/groupe-role';
import {SubscriptionI} from '../../shared/models/subscription-i';
import {PlayerFilter, SelectedPlayers} from '../models/player-filter';
import {licencesReducer, licencesSelectors, LicencesState} from './licence';
import {CommonField} from '../../shared/models/common-field';
import {programsReducer, programsSelectors, ProgramsState} from './program';
import {Product} from '../../shared/models/product';
import {productsReducer, productsSelectors, ProductsState} from '../../shared/reducers/products';
import { PublicationsReducer, PublicationsState, publicationsSelectors } from './publications-list';
import { Publication } from '../../shared/models/publication';
import { PublicationFilter } from '../models/publication-filter';
import {ProjectsReducer, projectsSelectors, ProjectsState} from './projects-list';
import {ConceptsBySubscriptionReducer, conceptsBySubscriptionSelectors, ConceptsBySubscriptionState} from './concepts-by-subscription';
import {ConceptsState} from '../../shared/reducers/concepts';
import {Concept} from '../../shared/models/concept';

export interface AppState extends fromRoot.AppState {
  sites: SitesState;
}

export interface SitesState {
  site: SiteState;
  sitesList: SitesListState;
  sitesSelected: SiteSelectedState;
  playersList: PlayersState;
  products: ProductsState;
  deviceModels: DeviceModelsState;
  licences: LicencesState;
  programs: ProgramsState;
  publicationsList: PublicationsState;
  projectsList: ProjectsState;
  conceptsBySubscription: ConceptsBySubscriptionState;
}

export const reducers: ActionReducerMap<SitesState> = {
  site: SiteReducer,
  sitesList: SitesListReducer,
  sitesSelected: SitesSelectedReducer,
  playersList: PlayersReducer,
  products: productsReducer,
  deviceModels: deviceModelsReducer,
  licences: licencesReducer,
  programs: programsReducer,
  publicationsList: PublicationsReducer,
  projectsList: ProjectsReducer,
  conceptsBySubscription: ConceptsBySubscriptionReducer
};

const getSiteState = createFeatureSelector('sites');

const rootSelectors = {
  site: createSelector(getSiteState, (state: SitesState) => _.get(state, 'site')),
  sitesList: createSelector(getSiteState, (state: SitesState) => _.get(state, 'sitesList')),
  sitesSelected: createSelector(getSiteState, (state: SitesState) => _.get(state, 'sitesSelected')),
  playersList: createSelector(getSiteState, (state: SitesState) => _.get(state, 'playersList')),
  deviceModels: createSelector(getSiteState, (state: SitesState) => _.get(state, 'deviceModels')),
  licences: createSelector(getSiteState, (state: SitesState) => _.get(state, 'licences')),
  programs: createSelector(getSiteState, (state: SitesState) => _.get(state, 'programs')),
  products: createSelector(getSiteState, (state: SitesState) => _.get(state, 'products')),
  publicationsList: createSelector(getSiteState, (state: SitesState) => _.get(state, 'publicationsList')),
  projectsList: createSelector(getSiteState, (state: SitesState) => _.get(state, 'projectsList')),
  conceptsBySubscription: createSelector(getSiteState, (state: SitesState) => _.get(state, 'conceptsBySubscription')),
};

export interface Selectors {
  getSiteFilter: MemoizedSelector<AppState, SiteFilter | string | number>;
  getSiteQuickSearch: MemoizedSelector<AppState, string>;
  getSiteSiteBy: MemoizedSelector<AppState, string>;
  getSite: MemoizedSelector<AppState, Site>;
  getSites: MemoizedSelector<AppState, Site[]>;
  getSiteByUuid: MemoizedSelectorWithProps<AppState, string, Site>;
  getSitesCount: MemoizedSelector<AppState, number>;
  getSitesSelected: MemoizedSelector<AppState, Site[]>;
  getSitePageSize: MemoizedSelector<AppState, number>;
  getSitePage: MemoizedSelector<AppState, number>;
  getSiteSelectedSites: MemoizedSelector<AppState, SelectedItem[]>;
  getSiteShowFilter: MemoizedSelector<AppState, boolean>;
  getPlayers: MemoizedSelector<AppState, Player[]>;
  getPlayersShowFilter: MemoizedSelector<AppState, boolean>;
  getPlayersCount: MemoizedSelector<AppState, number>;
  getPlayersSelected: MemoizedSelector<AppState, SelectedPlayers[]>;
  getDeviceModels: MemoizedSelector<AppState, GroupRole[]>;
  getSubscription: MemoizedSelector<AppState, SubscriptionI>;
  getLicences: MemoizedSelector<AppState, CommonField[]>;
  getPrograms: MemoizedSelector<AppState, CommonField[]>;
  getPlayersFilter: MemoizedSelector<AppState, PlayerFilter>;
  getProductsByService: MemoizedSelector<AppState, Product[]>;
  getProductsBySite: MemoizedSelector<AppState, Product[]>;
  getProducts: MemoizedSelector<AppState, Product[]>;
  getPublications: MemoizedSelector<AppState, Publication[]>;
  getPublicationsShowFilter: MemoizedSelector<AppState, boolean>;
  getPublicationsCount: MemoizedSelector<AppState, number>;
  getPublicationsFilter: MemoizedSelector<AppState, PublicationFilter>;
  // tslint:disable-next-line:no-any
  getProjects: MemoizedSelector<AppState, any[]>;
  getProjectsShowFilter: MemoizedSelector<AppState, boolean>;
  getProjectsCount: MemoizedSelector<AppState, number>;
  getProjectsFilter: MemoizedSelector<AppState, PublicationFilter>;
  getConceptsBySubscription: MemoizedSelector<AppState, Concept[]>;
}

export const selectors: Selectors = {
  getSiteFilter: createSelector(rootSelectors.sitesList, sitesListSelectors.filter),
  getSiteQuickSearch: createSelector(rootSelectors.sitesList, sitesListSelectors.quickSearch),
  getSiteSiteBy: createSelector(rootSelectors.sitesList, sitesListSelectors.orderBy),
  getSite: createSelector(rootSelectors.site, siteSelectors.site),
  getSites: createSelector(rootSelectors.sitesList, sitesListSelectors.array),
  getSiteByUuid: createSelector(rootSelectors.sitesList, (state: SitesListState, props: string) => sitesListSelectors.selectSiteByUuid(state, props)),
  getSitesCount: createSelector(rootSelectors.sitesList, sitesListSelectors.count),
  getSitesSelected: createSelector(rootSelectors.sitesSelected, sitesSelectedSelectors.sitesSelected),
  getSitePageSize: createSelector(rootSelectors.sitesList, sitesListSelectors.pageSize),
  getSitePage: createSelector(rootSelectors.sitesList, sitesListSelectors.page),
  getSiteSelectedSites: createSelector(rootSelectors.sitesList, sitesListSelectors.selectedSite),
  getSiteShowFilter: createSelector(rootSelectors.sitesList, sitesListSelectors.showFilter),
  getPlayers: createSelector(rootSelectors.playersList, playersSelectors.array),
  getPlayersShowFilter: createSelector(rootSelectors.playersList, playersSelectors.showFilter),
  getPlayersCount: createSelector(rootSelectors.playersList, playersSelectors.count),
  getPlayersSelected: createSelector(rootSelectors.playersList, playersSelectors.selectedPlayer),
  getDeviceModels: createSelector(rootSelectors.deviceModels, deviceModelsSelectors.array),
  getSubscription: createSelector(rootSelectors.site, siteSelectors.subscription),
  getLicences: createSelector(rootSelectors.licences, licencesSelectors.array),
  getPrograms: createSelector(rootSelectors.programs, programsSelectors.array),
  getProducts: createSelector(rootSelectors.products, productsSelectors.array),
  getPlayersFilter: createSelector(rootSelectors.playersList, playersSelectors.filter),
  getProductsByService: createSelector(rootSelectors.site, rootSelectors.products, (site, product) => siteSelectors.productsByService(site, product)),
  getProductsBySite: createSelector(rootSelectors.site, rootSelectors.products, (site, product) => siteSelectors.productsBySite(site, product)),
  getPublications: createSelector(rootSelectors.publicationsList, publicationsSelectors.array),
  getPublicationsShowFilter: createSelector(rootSelectors.publicationsList, publicationsSelectors.showFilter),
  getPublicationsCount: createSelector(rootSelectors.publicationsList, publicationsSelectors.count),
  getPublicationsFilter: createSelector(rootSelectors.publicationsList, publicationsSelectors.filter),
  getProjects: createSelector(rootSelectors.projectsList, projectsSelectors.array),
  getProjectsShowFilter: createSelector(rootSelectors.projectsList, projectsSelectors.showFilter),
  getProjectsCount: createSelector(rootSelectors.projectsList, projectsSelectors.count),
  getProjectsFilter: createSelector(rootSelectors.projectsList, projectsSelectors.filter),
  getConceptsBySubscription: createSelector(rootSelectors.conceptsBySubscription, conceptsBySubscriptionSelectors.array)
};
